import { useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import BaseTableCard from 'common/components/BaseTableCard'
import BasicTable from 'common/components/BasicTable'
import { DateRange } from 'common/components/DateRangePicker'
import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog'
import { FeatureKey } from 'common/constants/permission'
import {
  getCostKey,
  getCostTransactionsHistoryInfiniteKey,
  getFoodLineChartKey,
  getFoodTodayKey,
  getFoodsByRangeKey,
  getMealsByProductKey,
  getMealsKey,
  getRecentFoodByProductKey,
} from 'common/constants/queryKeys'
import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import { createArrayOfNumber } from 'common/utils/array'
import { formatThaiShortDateTwoDigitYear } from 'common/utils/date'
import { fNumber } from 'common/utils/formatNumber'
import { useMutateDeleteFoodByProduct } from 'modules/food/api/mutation'
import { useFoodsByRange } from 'modules/food/api/selector'
import { MealDetail } from 'modules/food/api/types'
import { MAX_MEAL } from 'modules/food/constants'
import { selectedFoodIdState, selectedMealIdState } from 'modules/food/stores/atom'
import { dashboardDateDisplayValue } from 'modules/food/utils'
import { useSpeciesName } from 'modules/pond/api/selector'
import { normalizeSpeciesName } from 'utils/analytics'
import MoreAction from 'modules/pond/components/MoreAction'
import { ReactNode, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useRecoilState, useSetRecoilState } from 'recoil'

import MealChip from '../MealChip'
import RecordFoodDialog from '../RecordFoodDialog'

const FoodTable = () => {
  const { t } = useTranslation(['common', 'food'])
  const qc = useQueryClient()
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
  })

  const {
    data: foods,
    isFetching: isFoodsFetching,
    refetch,
    hasNextPage,
    fetchNextPage,
  } = useFoodsByRange({
    startDate: dateRange.startDate ?? undefined,
    endDate: dateRange.endDate ?? undefined,
  })

  const [open, setOpen] = useState(false)

  const setMealId = useSetRecoilState(selectedMealIdState)
  const [foodId, setFoodId] = useRecoilState(selectedFoodIdState)

  const deleteFoodMutation = useMutateDeleteFoodByProduct()
  const speciesName = useSpeciesName()
  const { trackEvent } = useAnalytics()

  const onSuccess = (res: AxiosResponse) => {
    const { status } = res.data
    if (status === 200) {
      qc.invalidateQueries({ queryKey: getFoodsByRangeKey.all() })
      qc.invalidateQueries({ queryKey: getFoodLineChartKey.all() })
      qc.invalidateQueries({ queryKey: getFoodTodayKey.all() })
      qc.invalidateQueries({ queryKey: getRecentFoodByProductKey.all() })
      qc.invalidateQueries({ queryKey: getMealsKey.all() })
      qc.invalidateQueries({ queryKey: getMealsByProductKey.all() })
      qc.invalidateQueries({ queryKey: getCostKey.all() })
      qc.invalidateQueries({ queryKey: getCostTransactionsHistoryInfiniteKey.all() })
      const normalizedSpecies = normalizeSpeciesName(speciesName)
      trackEvent(
        AnalyticsEvents.feedRecordDeleted,
        normalizedSpecies ? { species: normalizedSpecies } : undefined
      )
    }
  }

  const { dialog, openDialog: openConfirmDialog } = useDeleteConfirmDialog({
    onConfirm: async () => {
      await deleteFoodMutation.mutateAsync(foodId || 0, { onSuccess })
    },
  })

  const mealColumns = createArrayOfNumber(MAX_MEAL).map((val) => {
    const meal = val + 1
    return {
      id: `meal${meal}`,
      title: <Trans i18nKey={'food:meal.number.table'} values={{ meal }} />,
      width: '160px',
    }
  })

  const getMealRow = (meals: MealDetail[]) => {
    let mealMap: Record<string, ReactNode> = {}
    createArrayOfNumber(MAX_MEAL).forEach((val) => {
      const mealNum = val + 1
      const meal = meals.find((el) => el.meal === mealNum)
      mealMap[`meal${mealNum}`] = meal ? <MealChip meal={meal} /> : '-'
    })
    return mealMap
  }

  const columns = [
    {
      id: 'date',
      title: t('common:date'),
      format: (val: string) => formatThaiShortDateTwoDigitYear(val),
      width: '150px',
    },
    {
      id: 'age',
      title: <Trans i18nKey={'pond:animal.age'} values={{ species: speciesName }} />,
      width: '100px',
      format: (val: number) => fNumber(val, { disabledDecimal: true }),
    },
    ...mealColumns,
    {
      id: 'totalRemain',
      title: t('food:total.remain.food.kg'),
      format: fNumber,
      width: '100px',
    },
    {
      id: 'totalFood',
      title: t('food:total.food.kg'),
      format: fNumber,
      width: '100px',
    },
    {
      id: 'accFood',
      title: t('food:acc.food.kg'),
      format: fNumber,
      width: '100px',
    },
    {
      id: 'action',
      title: '',
      width: '100px',
    },
  ]

  const handleClose = () => {
    setOpen(false)
  }

  const handleOpenEdit = (mealId: number, foodId: number) => {
    setMealId(mealId)
    setFoodId(foodId)
    setOpen(true)
  }

  const handleOpenDeleteConfirm = (id: number) => {
    setFoodId(id)
    openConfirmDialog()
  }

  const createTableRows = (
    date: string,
    age: number,
    meals: MealDetail[],
    totalRemain: number,
    totalFood: number,
    accFood: number,
    action: {
      mealId?: number
      foodId: number
    }
  ) => ({
    date,
    age,
    ...getMealRow(meals),
    totalRemain,
    totalFood,
    accFood,
    action: (
      <MoreAction
        featureKey={FeatureKey.Food}
        isHideOnCompleted={false}
        onClickEdit={() => action.mealId && handleOpenEdit(action.mealId, action.foodId)}
        onClickDelete={() => handleOpenDeleteConfirm(action.foodId)}
      />
    ),
  })

  const rows = foods?.map((row) =>
    createTableRows(
      row.date,
      row.shrimpAge,
      row.meals || [],
      row.totalRemainingFood,
      row.totalFood,
      row.accumulatedFood,
      {
        foodId: row.id,
        mealId: row.meals?.length ? row.meals[row.meals.length - 1].id : undefined,
      }
    )
  )

  const onLoadMoreRows = () => {
    if (hasNextPage) {
      fetchNextPage()
    }
  }

  return (
    <>
      {dialog}
      <BaseTableCard
        title={t('food:feeding.recap.per.day')}
        refresh={() => {
          qc.invalidateQueries({ queryKey: getFoodsByRangeKey.all() })
        }}
        isLoading={isFoodsFetching && foods.length === 0}
        isEmpty={foods.length === 0 && !hasNextPage}
        isDateRangePicker={true}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        displayValue={dashboardDateDisplayValue}
      >
        <BasicTable
          columns={columns}
          rows={rows || []}
          onLoading={onLoadMoreRows}
          isLoading={isFoodsFetching}
        />
      </BaseTableCard>
      <RecordFoodDialog open={open} onClose={handleClose} isEdit />
    </>
  )
}

export default FoodTable
